import { useState } from 'react';

import { WidgetViewMode, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { UseBaseTable } from 'hooks/useBaseTable';
import useCustomExcelFileName from 'hooks/useCustomExcelFileName';
import { useWidgetPillsAndFilters } from 'hooks/useWidgetPillsAndFilters';

import { useMutationExportCsvReport } from 'api/mutations/exportCsvReport.mutation';
import { useQueryLargestDeletedActivitiesTableData } from 'api/queries/largestDeletedActivitiesTableData.query';

import { TKeys } from 'components/Dashboards/Project/Components/VersionCompare/utils/helper';

export type UseLargestDeletedActivitiesProvider = ReturnType<typeof useLargestDeletedActivitiesProvider>;

interface Props {
    componentKey: string;
    widgetId?: string;
    globalFilterData: any;
    localFilters: any;
    compareVersionId: number | undefined;
    latestVersionId: number | undefined;
    title: string;
    projectId: number | null | undefined;
    contractId: number | null | undefined;
    table: UseBaseTable['table'];
    widgetViewMode: WidgetViewMode;
    endpoints: Record<string, string>;
}

export const useLargestDeletedActivitiesProvider = ({
    widgetId,
    globalFilterData,
    localFilters,
    compareVersionId,
    latestVersionId,
    title,
    projectId,
    contractId,
    widgetViewMode,
    endpoints,
}: Props) => {
    const isInPinboard = Boolean(widgetId);
    const [activeTab, setActiveTab] = useState<TKeys>(
        (WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ||
            WidgetViewModeUtils.isReportOrPinboardView(widgetViewMode)) &&
            localFilters?.activeTab
            ? (localFilters?.activeTab as TKeys)
            : 'next',
    );
    const source = isInPinboard ? { ...localFilters, activeTab } : { ...globalFilterData, activeTab };

    const { filters, pills } = useWidgetPillsAndFilters({ source, widgetId, latestVersionId });

    const getLargestDeletedActivitiesTableDataPayload = {
        projectId: projectId,
        contractId: contractId,
        latestVersionId: latestVersionId,
        compareVersionId: compareVersionId,
        filter: {
            filters,
        },
    };

    const getLargestDeletedActivitiesTableData = useQueryLargestDeletedActivitiesTableData(
        getLargestDeletedActivitiesTableDataPayload,
        endpoints.getLongestDeletedActivities,
    );

    const reportType = getLargestDeletedActivitiesTableData.data?.typeId || '';
    const fileName = useCustomExcelFileName({
        projectId,
        contractId,
        latestVersionId,
        title,
        prefix: 'Change_Analysis_',
    });

    const exportCsvReport = {
        instance: useMutationExportCsvReport(),
        payload: {
            compareVersionId,
            contractId,
            filter: { filters },
            latestVersionId,
            period: activeTab,
            projectId,
            reportType,
            fileName,
        },
    };

    return {
        ui: {
            state: {
                activityCodes: source?.activityCodes,
                milestoneFragnet: source?.milestoneFragnet,
                tags: source?.tags,
                wbs: source?.wbs,
                activeTab: source?.activeTab,
            },
            setters: {
                setActiveTab,
            },
            isInPinboard,
            pills,
        },
        api: {
            getLargestDeletedActivitiesTableData: {
                instance: getLargestDeletedActivitiesTableData,
                payload: getLargestDeletedActivitiesTableDataPayload,
                endpoint: endpoints.getLongestDeletedActivities,
            },
            exportCsvReport,
        },
    };
};
